import { useState, useEffect, useCallback } from "react";
import { ArrowRight, PhoneCall, X } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Global sticky conversion bar.
 *
 * Shows after 600px of scroll on every page. Presents two low-friction next
 * steps side-by-side — tap-to-call (freephone, non-form exit) + primary
 * savings CTA — so campaign traffic always has both options in view. Uses
 * the same rounded-full pill vocabulary, Aqua/black brand colours, and
 * padding rhythm as the rest of the site's CTAs so it feels native.
 * Per-session dismissal (no localStorage) — resets on each fresh visit.
 */
export default function StickyQuoteBar() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const handleScroll = useCallback(() => {
    if (dismissed) return;
    setVisible(window.scrollY > 600);
  }, [dismissed]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  if (!visible || dismissed) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 bg-brand-black/95 backdrop-blur-md border-t border-white/10 py-3 px-4 animate-slide-up"
      data-testid="sticky-quote-bar"
      role="complementary"
      aria-label="Quick actions"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        {/* Desktop-only context line. Kept short to leave room for both CTAs. */}
        <p className="hidden lg:block text-sm font-semibold text-white">
          Ready to switch? See how much you could save.
        </p>

        <div className="flex items-center gap-3 flex-1 lg:flex-none justify-end">
          {/* Secondary: tap-to-call. Ghost-style pill on mobile (equal weight to the
              primary CTA so cold traffic has a real choice) and a subtle text link
              on desktop where the phone number is already in the navbar. */}
          <a
            href="tel:+448003682989"
            className="lg:hidden inline-flex items-center gap-1.5 rounded-full border border-white/30 text-white hover:bg-white/10 font-semibold px-5 py-2.5 text-sm transition-colors"
            data-testid="sticky-cta-call"
            aria-label="Call CanDo Payments on 0800 368 2989"
          >
            <PhoneCall size={14} />
            Call free
          </a>
          <a
            href="tel:+448003682989"
            className="hidden lg:inline-flex items-center gap-1.5 text-white/80 hover:text-white font-semibold text-sm transition-colors"
            data-testid="sticky-cta-call-desktop"
          >
            <PhoneCall size={13} /> 0800 368 2989
          </a>

          {/* Primary CTA — matches site-wide primary button padding (px-6 py-2.5)
              so the bar reads as native, not as a bolt-on mini version. */}
          <Link
            to="/switch-and-save#calculator"
            className="cta-primary text-white font-semibold rounded-full px-6 py-2.5 text-sm flex items-center gap-2 transition-all hover:gap-3 whitespace-nowrap"
            data-testid="sticky-cta-button"
          >
            See My Savings <ArrowRight size={14} />
          </Link>

          <button
            onClick={() => setDismissed(true)}
            className="text-white/50 hover:text-white transition-colors p-1"
            data-testid="sticky-dismiss"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
